import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { showErrorToast, showSuccessToast } from 'components/Toast';
import PrivateRoute from 'components/Navigation/ProtectedRoute';
import OutlineButton from 'components/OutlineButton';
import BankModalContent from './ActionModalContent';
import SolidButton from 'components/SolidButton';
import Modal from 'components/Modal';
import Table from 'components/Table';

import { IBank } from 'utils/interfaces';
import { BUTTON_COLORS } from 'utils/enums';
import GeneralApi from 'utils/generalApi';
import { useAuth } from 'contexts/Auth';

export default function BanksPage() {
  const history = useNavigate();
  const auth = useAuth();

  const generalApi = useMemo(() => new GeneralApi(auth, history), [auth, history]);

  const [refresh, setRefresh] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingId, setLoadingId] = useState('');
  const [banks, setBanks] = useState<Array<IBank>>([]);

  const [isActionOpen, setIsActionOpen] = useState(false);
  const [actionData, setActionData] = useState<IBank | undefined>(undefined);

  const toggleBank = async (bankId: string, isActive: boolean) => {
    setLoadingId(bankId);
    let result: any = await generalApi.post('/toggle_bank', {
      bankId,
    });
    setLoadingId('');
    if (!result.success) return showErrorToast(result.data.err);
    showSuccessToast(isActive ? 'Banco deshabilitado' : 'Banco habilitado');
    setRefresh(true);
  };

  useEffect(() => {
    const getBanks = async () => {
      setLoading(true);
      const result = await generalApi.get('/banks');
      if (!result.success) {
        showErrorToast(result.message);
        setLoading(false);
        return;
      }
      setBanks(result.data);
      setLoading(false);
    };
    if (refresh) {
      getBanks();
      setRefresh(false);
    }
  }, [generalApi, refresh]);

  return (
    <PrivateRoute>
      <div className="text-[#1B205A]">
        <Modal
          isOpen={isActionOpen}
          onClose={() => {
            setIsActionOpen(false);
            setActionData(undefined);
          }}
        >
          <BankModalContent
            title={actionData ? 'Editar banco' : 'Crear banco'}
            editData={actionData}
            onClose={() => {
              setIsActionOpen(false);
              setActionData(undefined);
            }}
            onSuccess={() => {
              setRefresh(true);
              setIsActionOpen(false);
              setActionData(undefined);
            }}
          />
        </Modal>
        <div className="pb-8 2xl:px-24">
          <div className="mt-6 justify-between space-y-4 px-8 pb-4 sm:flex sm:space-y-0">
            <p className="text-2xl font-bold">Lista de Bancos</p>
            <div className="space-y-2 sm:ml-20 sm:flex sm:space-y-0 sm:space-x-4">
              <OutlineButton color={BUTTON_COLORS.BLUE} onClick={() => setIsActionOpen(true)}>
                Nuevo Banco
              </OutlineButton>
            </div>
          </div>
          <Table
            isLoading={loading}
            loadingText="Buscando operaciones"
            columns={[
              {
                title: 'CÓDIGO',
                dataKey: 'code',
              },
              {
                title: 'Nombre',
                dataKey: 'name',
              },
              {
                title: 'Monto Mínimo',
                dataKey: 'min_amount',
              },
              {
                title: 'Monto Máximo',
                dataKey: 'max_amount',
              },
              {
                title: 'Límite - Ahorros',
                dataKey: 'A_length',
              },
              {
                title: 'Límite - Corriente',
                dataKey: 'C_length',
              },
              {
                title: 'Interbancario',
                dataKey: 'is_interbank',
                render: (item: IBank) => (item.is_interbank ? 'Si' : 'No'),
              },
              {
                title: 'OPCIONES',
                dataKey: 'options',
                disableSort: true,
                render: (item: IBank) => (
                  <div className="flex space-x-2 pr-2">
                    <SolidButton
                      color={BUTTON_COLORS.BLUE}
                      onClick={() => {
                        setIsActionOpen(true);
                        setActionData(item);
                      }}
                    >
                      Editar
                    </SolidButton>
                    <SolidButton
                      isLoading={loadingId === item._id}
                      color={item.is_active ? BUTTON_COLORS.RED : BUTTON_COLORS.BLUE}
                      onClick={() => toggleBank(item._id, item.is_active)}
                    >
                      {item.is_active ? 'Deshabilitar' : 'Habilitar'}
                    </SolidButton>
                  </div>
                ),
              },
            ]}
            data={banks}
          />
        </div>
      </div>
    </PrivateRoute>
  );
}
